import { useState, useMemo, useRef, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import {
  HiOutlineBuildingOffice,
  HiOutlineClipboardDocumentList,
  HiOutlineChartBar,
  HiOutlineArrowLeft,
  HiOutlineArrowPath,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
  HiOutlineUserGroup,
} from "react-icons/hi2";
import { FaChartBar, FaBuilding, FaCalendarAlt } from "react-icons/fa";
import { GrScorecard } from "react-icons/gr";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import Button from "components/button/Button";
import {
  useCompleteOrganizationAssessmentMutation,
  useGetOrganizationAssessmentByIdQuery,
} from "reduxFiles/api/assessmentApi";
import moment from "moment-jalaali";
import { useScope } from "hooks/useScope";
import ConfirmModal from "components/modal/ConfirmModal";
import { useModal } from "hooks/useModal";
import IndustryLifeCycle from "views/company/assessments/report/industryLifeCycle/IndustryLifeCycle";
import { HiOutlineExternalLink } from "react-icons/hi";
import { OrganizationAssessmentStatus } from "types/assessmentTypes";

export default function OrganizationAssessmentReport() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const scope = useScope();
  const confirmModal = useModal();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);
  const [chartKey, setChartKey] = useState(0);
  const [isDark, setIsDark] = useState(
    document.body.classList.contains("dark")
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.body.classList.contains("dark"));
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const [expandedResponderId, setExpandedResponderId] = useState<number | null>(
    null
  );

  // Helper to map category to color and label
  const getCategoryStyle = (category: string) => {
    const map: Record<
      string,
      { color: string; bg: string; border: string; label: string }
    > = {
      A: {
        color: "text-green-700",
        bg: "bg-green-100",
        border: "border-green-300",
        label: t(`organizationAssessmentReport.categories.A`),
      },
      B: {
        color: "text-lime-700",
        bg: "bg-lime-100",
        border: "border-lime-300",
        label: t(`organizationAssessmentReport.categories.B`),
      },
      C: {
        color: "text-blue-700",
        bg: "bg-blue-100",
        border: "border-blue-300",
        label: t(`organizationAssessmentReport.categories.C`),
      },
      D: {
        color: "text-cyan-700",
        bg: "bg-cyan-100",
        border: "border-cyan-300",
        label: t(`organizationAssessmentReport.categories.D`),
      },
      E: {
        color: "text-orange-700",
        bg: "bg-orange-100",
        border: "border-orange-300",
        label: t(`organizationAssessmentReport.categories.E`),
      },
      F: {
        color: "text-red-700",
        bg: "bg-red-100",
        border: "border-red-300",
        label: t(`organizationAssessmentReport.categories.F`),
      },
    };
    return map[category] || map.F;
  };

  // Get assessment details
  const {
    data: assessment,
    isFetching: isFetchingAssessment,
    isError,
    refetch,
  } = useGetOrganizationAssessmentByIdQuery(
    { scope, id: Number(id) },
    {
      skip: !id,
    }
  );

  const handleRefetch = async () => {
    await refetch();
    setChartKey((prev) => prev + 1);
  };

  const [completeOrganizationAssessment, { isLoading: isCompleting }] =
    useCompleteOrganizationAssessmentMutation();

  const handleCompleteOrganizationAssessment = async () => {
    try {
      await completeOrganizationAssessment({ id: Number(id) }).unwrap();
      confirmModal.closeModal();
      refetch();
    } catch (err) {
      console.error(err);
    }
  };

  const isPending = assessment?.status === "PENDING";
  const isCompleted = assessment?.status === "COMPLETED";
  const hasAnswers = assessment?.answers && assessment.answers.length > 0;

  // Get company assessment from assessment.assessment
  const companyAssessment = assessment?.assessment;
  const hasCompanyAssessment =
    companyAssessment && companyAssessment.status === "COMPLETED";

  // ----- Responders with answers -----
  const respondersWithStatus = useMemo(() => {
    if (!assessment?.responders) return [];

    const answersMap = new Map();
    assessment.answers?.forEach((answer) => {
      const responderId = answer.responding?.responder?.id;
      if (responderId) {
        if (!answersMap.has(responderId)) {
          answersMap.set(responderId, []);
        }
        answersMap.get(responderId).push({
          questionName: answer.question?.name || "",
          optionName: answer.option?.name || "",
          optionKey: answer.option?.key || "",
          optionValue: answer.option?.value || 0,
          description: answer.description || "",
        });
      }
    });

    return assessment.responders.map((responder) => ({
      ...responder,
      answers: answersMap.get(responder.id) || [],
    }));
  }, [assessment]);

  // Prepare chart data from scores
  const chartData = useMemo(() => {
    if (!assessment?.scores || assessment.scores.length === 0) {
      return null;
    }

    // Sort by question index
    const sortedScores = [...assessment.scores].sort(
      (a, b) => (a.question?.index || 0) - (b.question?.index || 0)
    );

    return sortedScores.map((score) => ({
      label: score.question?.key || "",
      value: +score.normalizeAverage?.toFixed(2) || 0,
      questionId: score.questionId,
    }));
  }, [assessment]);

  // Initialize chart
  useEffect(() => {
    if (isCompleted && chartRef.current && chartData && chartData.length > 0) {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }

      const ctx = chartRef.current.getContext("2d");
      if (!ctx) return;

      const data = chartData.map((item) => item.value);
      const labels = chartData.map((item) => {
        const maxLength = 50;
        const label = item.label;
        return label.length > maxLength
          ? label.substring(0, maxLength) + "..."
          : label;
      });

      const colorPalette = [
        "#6366F1",
        "#8B5CF6",
        "#3B82F6",
        "#06B6D4",
        "#10B981",
        "#F59E0B",
        "#EF4444",
        "#EC4899",
        "#14B8A6",
        "#F97316",
      ];

      const backgroundColors = data.map(
        (_, index) => colorPalette[index % colorPalette.length] + "CC"
      );
      const borderColors = data.map(
        (_, index) => colorPalette[index % colorPalette.length]
      );

      const maxVal = Math.max(...data, 0);
      const yMax = maxVal === 0 ? 10 : maxVal + Math.max(2, maxVal * 0.15);

      chartInstanceRef.current = new Chart(ctx, {
        type: "bar",
        data: {
          labels: labels,
          datasets: [
            {
              label: t("organizationAssessmentReport.IEII"),
              data: data,
              backgroundColor: backgroundColors,
              borderColor: borderColors,
              borderWidth: 2,
              borderRadius: 6,
              maxBarThickness: 28,
              hoverBackgroundColor: borderColors,
            },
          ],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 20,
              bottom: 10,
              right: lang === "fa" ? 10 : 40,
              left: lang === "fa" ? 40 : 10,
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              rtl: lang === "fa",
              backgroundColor: "rgba(255,255,255,0.96)",
              titleColor: "#1F2937",
              bodyColor: "#374151",
              borderColor: "#E5E7EB",
              borderWidth: 1,
              padding: 12,
              cornerRadius: 12,
              titleFont: {
                family: Chart.defaults.font.family,
              },
              bodyFont: {
                family: Chart.defaults.font.family,
              },
              callbacks: {
                label: function (context) {
                  return context.parsed.x?.toFixed(2) || "0";
                },
              },
            },
            datalabels: {
              anchor: "end",
              align: "end",
              color: isDark ? "#eee" : "#374151",
              font: {
                weight: "bold",
                size: 11,
                family: Chart.defaults.font.family,
              },
              formatter: function (value: number) {
                return value?.toFixed(2) || 0;
              },
              ...(lang === "fa" && {
                anchor: "end",
                align: "end",
                offset: 25,
              }),
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              max: yMax,
              grid: {
                color: isDark ? "#374151" : "rgba(0,0,0,0.06)",
              },
              ticks: {
                stepSize: Math.max(1, Math.ceil(yMax / 8)),
                callback: function (value) {
                  return (+value)?.toFixed(1) || 0;
                },
                color: isDark && "#eee",
                font: {
                  size: 11,
                  family: Chart.defaults.font.family,
                },
              },
            },
            y: {
              grid: {
                display: true,
                color: isDark ? "#374151" : "rgba(0,0,0,0.04)",
              },
              ticks: {
                maxRotation: 0,
                color: isDark && "#eee",
                font: {
                  size: 12,
                  weight: 500,
                  family: Chart.defaults.font.family,
                },
              },
            },
          },
        },
        plugins: [ChartDataLabels],
      });
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [isCompleted, chartData, t, lang, chartKey, isDark]);

  useEffect(() => {
    const fontFamily =
      getComputedStyle(document.body).fontFamily ||
      (lang === "fa" || lang === "ar"
        ? "IRANSans, Vazir, Tahoma, sans-serif"
        : "Inter, sans-serif");
    Chart.defaults.font.family = fontFamily;
  }, [lang]);

  // Stats Cards - Replace responders with company assessment info
  const statCards = useMemo(() => {
    const baseCards = [
      {
        key: "organization",
        icon: FaBuilding,
        iconClass:
          "rounded-xl bg-brand-50 p-3 text-brand-600 group-hover:bg-brand-100 dark:bg-brand-500/20 dark:text-brand-400 dark:group-hover:bg-brand-500/30",
        label: t("organizationAssessmentReport.organization"),
        value: assessment?.organization?.name || "-",
      },
      {
        key: "company",
        icon: HiOutlineBuildingOffice,
        iconClass:
          "rounded-xl bg-blue-50 p-3 text-blue-600 group-hover:bg-blue-100 dark:bg-blue-500/20 dark:text-blue-400 dark:group-hover:bg-blue-500/30",
        label: t("organizationAssessmentReport.company"),
        value: assessment?.company?.name || "-",
      },
      {
        key: "industrySector",
        icon: HiOutlineChartBar,
        iconClass:
          "rounded-xl bg-purple-50 p-3 text-purple-600 group-hover:bg-purple-100 dark:bg-purple-500/20 dark:text-purple-400 dark:group-hover:bg-purple-500/30",
        label: t("organizationAssessmentReport.industrySector"),
        value:
          t(`industrySectors.${assessment?.company?.industrySector?.key}`) ??
          "-",
      },
    ];

    // Add company assessment info if exists
    if (hasCompanyAssessment) {
      baseCards.push({
        key: "companyAssessment",
        icon: FaCalendarAlt,
        iconClass:
          "rounded-xl bg-green-50 p-3 text-green-600 group-hover:bg-green-100 dark:bg-green-500/20 dark:text-green-400 dark:group-hover:bg-green-500/30",
        label: t("organizationAssessmentReport.lastCompanyAssessment"),
        value: moment(
          companyAssessment.completedAt || companyAssessment.createdAt
        ).format(lang === "fa" ? "jYYYY/jMM/jDD" : "YYYY/MM/DD"),
      });
    } else {
      baseCards.push({
        key: "companyAssessment",
        icon: HiOutlineXCircle,
        iconClass:
          "rounded-xl bg-gray-50 p-3 text-gray-600 group-hover:bg-gray-100 dark:bg-gray-500/20 dark:text-gray-400 dark:group-hover:bg-gray-500/30",
        label: t("organizationAssessmentReport.lastCompanyAssessment"),
        value: t("organizationAssessmentReport.noCompanyAssessment"),
      });
    }

    return baseCards;
  }, [assessment, hasCompanyAssessment, companyAssessment, lang, t]);

  // Early returns
  if (isFetchingAssessment) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="loader w-8 text-brand-500" />
      </div>
    );
  }

  if (isError || !assessment) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <HiOutlineClipboardDocumentList className="mx-auto h-16 w-16 text-gray-400" />
          <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
            {t("organizationAssessmentReport.notFound")}
          </h3>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-400">
            {t("organizationAssessmentReport.notFoundDesc")}
          </p>
          <Button
            variant="primary"
            className="mt-4"
            onClick={() => navigate(-1)}
          >
            {t("organizationAssessmentReport.back")}
          </Button>
        </div>
      </div>
    );
  }

  const showCompleteButton = isPending && scope === "organization";

  const categoryStyle = assessment.category
    ? getCategoryStyle(assessment.category)
    : null;

  // Get OHI from company assessment
  const ohiScore = assessment.companyOhi;
  const hasOhi = ohiScore !== undefined && ohiScore !== null;

  return (
    <div className="min-h-screen bg-gradient-to-br p-2 md:p-4">
      <div className="container mx-auto max-w-7xl">
        {/* 1. Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 flex flex-wrap items-center justify-between gap-4"
        >
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-all hover:bg-gray-100 hover:text-brand-600 dark:text-gray-400 dark:hover:bg-navy-800 dark:hover:text-brand-400"
            >
              <HiOutlineArrowLeft className="h-4 w-4 rtl:rotate-180" />
              {t("assessment.detail.back")}
            </button>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t("organizationAssessmentReport.title")}
              </h1>
              {isCompleted && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700 dark:bg-green-500/20 dark:text-green-400">
                  <HiOutlineCheckCircle className="h-3 w-3" />
                  {t("organizationAssessmentReport.statuses.COMPLETED")}
                </span>
              )}
              {isPending && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700 dark:bg-amber-500/20 dark:text-amber-400">
                  <HiOutlineXCircle className="h-3 w-3" />
                  {t("organizationAssessmentReport.statuses.PENDING")}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {showCompleteButton && (
              <Button
                variant="success"
                size="sm"
                onClick={confirmModal.openModal}
                disabled={!hasAnswers}
                startIcon={<HiOutlineCheckCircle className="h-4 w-4" />}
              >
                {t("organizationAssessmentReport.completeAssessment")}
              </Button>
            )}

            <button
              onClick={handleRefetch}
              disabled={isFetchingAssessment}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-all hover:bg-gray-100 hover:text-brand-600 dark:text-gray-400 dark:hover:bg-navy-800 dark:hover:text-brand-400"
            >
              <HiOutlineArrowPath
                className={`h-4 w-4 ${
                  isFetchingAssessment ? "animate-spin" : ""
                }`}
              />
              {t("assessment.detail.refresh")}
            </button>
          </div>
        </motion.div>

        {/* 2. Stats Cards - 4 cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-8"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {statCards.map((card, index) => (
              <motion.div
                key={card.key}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 + 0.2 }}
                whileHover={{ y: -4, scale: 1.02 }}
                className="group rounded-2xl bg-white/80 p-5 shadow-lg shadow-gray-200/50 backdrop-blur-sm transition-all hover:shadow-xl dark:bg-navy-800/80 dark:shadow-navy-900/50"
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`transition-all duration-300 ${card.iconClass}`}
                  >
                    <card.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-400">
                      {card.label}
                    </p>
                    <p className="mt-1 text-sm font-bold text-gray-900 dark:text-white">
                      {card.value}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* 3. Category & Scores Card - Redesigned with Animations */}
        {assessment && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mb-8"
          >
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white via-brand-50/30 to-purple-50/30 p-6 shadow-2xl shadow-brand-500/10 backdrop-blur-sm dark:from-navy-800 dark:via-navy-700 dark:to-navy-800 dark:shadow-brand-500/5">
              {/* Animated gradient background */}
              <motion.div
                className="absolute -inset-1 bg-gradient-to-r from-brand-500/10 via-purple-500/10 to-pink-500/10"
                animate={{
                  background: [
                    "radial-gradient(circle at 0% 0%, rgba(99,102,241,0.1) 0%, transparent 50%)",
                    "radial-gradient(circle at 100% 100%, rgba(168,85,247,0.1) 0%, transparent 50%)",
                    "radial-gradient(circle at 50% 50%, rgba(236,72,153,0.1) 0%, transparent 50%)",
                    "radial-gradient(circle at 0% 0%, rgba(99,102,241,0.1) 0%, transparent 50%)",
                  ],
                }}
                transition={{ duration: 10, repeat: Infinity }}
              />

              {/* Decorative floating particles */}
              <motion.div
                className="absolute right-20 top-10 h-32 w-32 rounded-full bg-brand-500/5 blur-3xl"
                animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 4, repeat: Infinity }}
              />
              <motion.div
                className="absolute bottom-10 left-20 h-40 w-40 rounded-full bg-purple-500/5 blur-3xl"
                animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.5, 0.2] }}
                transition={{ duration: 5, repeat: Infinity, delay: 1 }}
              />

              <div className="relative flex flex-col items-center gap-6 lg:flex-row lg:items-stretch">
                {/* Left Section - Status Info (Full width on mobile, 50% on desktop) */}
                <div className="flex w-full flex-1 flex-col items-center justify-center gap-4 lg:w-1/2">
                  <div className="flex w-full items-center gap-4">
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{
                        type: "spring",
                        stiffness: 200,
                        delay: 0.4,
                      }}
                      className={`flex-shrink-0 rounded-2xl p-3 shadow-lg ring-2 ring-white/20 dark:ring-white/10 ${
                        assessment.category && categoryStyle
                          ? `${categoryStyle.bg} ${categoryStyle.color}`
                          : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                      }`}
                    >
                      <GrScorecard className="h-8 w-8 md:h-10 md:w-10" />
                    </motion.div>

                    <div className="min-w-0 flex-1">
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 }}
                        className="flex flex-wrap items-baseline gap-2"
                      >
                        <motion.span
                          initial={{ scale: 0.5, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{
                            type: "spring",
                            stiffness: 200,
                            delay: 0.6,
                          }}
                          className={`text-xl font-black md:text-2xl lg:text-3xl ${
                            assessment.category && categoryStyle
                              ? categoryStyle.color
                              : "text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {assessment.decisionStatus && isCompleted
                            ? t(
                                `organizationAssessmentReport.statusCard.title.${assessment.decisionStatus}`
                              )
                            : isPending
                            ? t(
                                "organizationAssessmentReport.statusCard.pending"
                              )
                            : t(
                                "organizationAssessmentReport.statusCard.noDataTitle"
                              )}
                        </motion.span>

                        {/* Status badge */}
                        {isCompleted && assessment.decisionStatus && (
                          <motion.span
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.7 }}
                            className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                              assessment.decisionStatus ===
                              OrganizationAssessmentStatus.MAINTAIN
                                ? "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400"
                                : assessment.decisionStatus ===
                                  OrganizationAssessmentStatus.TERMINATION
                                ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400"
                                : assessment.decisionStatus ===
                                    OrganizationAssessmentStatus.SERIOUS_WARNING ||
                                  assessment.decisionStatus ===
                                    OrganizationAssessmentStatus.WARNING
                                ? "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400"
                                : assessment.decisionStatus ===
                                  OrganizationAssessmentStatus.OPPORTUNITY
                                ? "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400"
                                : "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400"
                            }`}
                          >
                            {assessment.decisionStatus ===
                            OrganizationAssessmentStatus.MAINTAIN
                              ? "✅"
                              : assessment.decisionStatus ===
                                OrganizationAssessmentStatus.TERMINATION
                              ? "⛔"
                              : assessment.decisionStatus ===
                                  OrganizationAssessmentStatus.SERIOUS_WARNING ||
                                assessment.decisionStatus ===
                                  OrganizationAssessmentStatus.WARNING
                              ? "⚠️"
                              : "🔄"}
                          </motion.span>
                        )}

                        {/* PENDING */}
                        {isPending && (
                          <motion.span
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.7 }}
                            className="flex-shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:bg-amber-500/20 dark:text-amber-400"
                          >
                            ⏳{" "}
                            {t("organizationAssessmentReport.statuses.PENDING")}
                          </motion.span>
                        )}
                      </motion.div>

                      <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8 }}
                        className="mt-2 text-xs text-gray-800 dark:text-gray-300 md:text-sm"
                      >
                        {assessment.decisionStatus && isCompleted
                          ? t(
                              `organizationAssessmentReport.statusCard.description.${assessment.decisionStatus}`
                            )
                          : isPending
                          ? t(
                              "organizationAssessmentReport.statusCard.pendingDesc"
                            )
                          : t("organizationAssessmentReport.statusCard.noData")}
                      </motion.p>
                    </div>
                  </div>
                </div>

                {/* Right Section - Donut Charts (Full width on mobile, 50% on desktop) */}
                <div className="flex w-full flex-1 items-center justify-center gap-4 lg:w-1/2">
                  {/* Divider line (only on desktop) */}
                  <div className="from-transparent to-transparent hidden h-24 w-px bg-gradient-to-b via-gray-300 dark:via-gray-600 lg:block" />

                  <div className="flex w-full flex-wrap items-center justify-center gap-6 sm:gap-8">
                    {/* IEII Donut Chart */}
                    {isCompleted && assessment.finalScore !== undefined && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5, type: "spring" }}
                        className="group flex flex-col items-center"
                      >
                        <div className="mb-1 flex items-center gap-2">
                          <span
                            className={`inline-block h-2 w-2 rounded-full ${
                              assessment.category && categoryStyle
                                ? categoryStyle.color.replace("text", "bg")
                                : "bg-gray-400"
                            }`}
                          />
                          <span className="text-xs font-medium text-gray-800 dark:text-gray-300">
                            {t("organizationAssessmentReport.IEII")} ( IEII )
                          </span>
                        </div>

                        <div className="relative h-24 w-24 transition-transform duration-300 group-hover:scale-105 sm:h-28 sm:w-28 md:h-32 md:w-32">
                          <div
                            className="absolute -inset-1 rounded-full opacity-50 blur-lg transition-opacity duration-300 group-hover:opacity-100"
                            style={{
                              background: `radial-gradient(circle, ${
                                assessment.category === "A" ||
                                assessment.category === "B"
                                  ? "rgba(34,197,94,0.2)"
                                  : assessment.category === "C" ||
                                    assessment.category === "D"
                                  ? "rgba(59,130,246,0.2)"
                                  : assessment.category === "E" ||
                                    assessment.category === "F"
                                  ? "rgba(249,115,22,0.2)"
                                  : "rgba(99,102,241,0.2)"
                              }, transparent 70%)`,
                            }}
                          />

                          <svg
                            className="relative h-full w-full -rotate-90"
                            viewBox="0 0 100 100"
                          >
                            <circle
                              cx="50"
                              cy="50"
                              r="38"
                              fill="none"
                              stroke="#e5e7eb"
                              strokeWidth="8"
                              className="dark:stroke-navy-600"
                            />
                            <circle
                              cx="50"
                              cy="50"
                              r="38"
                              fill="none"
                              stroke={
                                assessment.category === "A" ||
                                assessment.category === "B"
                                  ? "#22c55e"
                                  : assessment.category === "C" ||
                                    assessment.category === "D"
                                  ? "#3b82f6"
                                  : assessment.category === "E" ||
                                    assessment.category === "F"
                                  ? "#f97316"
                                  : "#6366f1"
                              }
                              strokeWidth="8"
                              strokeLinecap="round"
                              strokeDasharray={`${
                                (assessment.finalScore || 0) * 2.387
                              } 238.76`}
                              strokeDashoffset="0"
                              className="transition-all duration-1000"
                            />
                          </svg>

                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <motion.span
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: 0.6, type: "spring" }}
                              className="text-sm font-extrabold text-gray-900 dark:text-white sm:text-base"
                            >
                              {assessment.finalScore?.toFixed(1) || 0}
                            </motion.span>
                            <span className="text-[12px] font-medium text-gray-700 dark:text-gray-400 md:text-sm">
                              %
                            </span>
                          </div>
                        </div>

                        <div className="mt-1 text-center">
                          <span
                            className={`text-[10px] font-bold sm:text-xs ${
                              assessment.category && categoryStyle
                                ? categoryStyle.color
                                : "text-gray-500"
                            }`}
                          >
                            {assessment.category && categoryStyle
                              ? categoryStyle.label
                              : "-"}
                          </span>
                        </div>
                      </motion.div>
                    )}

                    {/* OHI Donut Chart */}
                    {hasOhi ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.7, type: "spring" }}
                        className="group flex flex-col items-center"
                      >
                        <div className="mb-1 flex items-center gap-2">
                          <span
                            className={`inline-block h-2 w-2 rounded-full ${
                              ohiScore >= 60
                                ? "bg-green-500"
                                : ohiScore >= 40
                                ? "bg-yellow-500"
                                : "bg-red-500"
                            }`}
                          />
                          <span className="text-xs font-medium text-gray-800 dark:text-gray-300">
                            {t("assessment.report.OHI")} ( OHI )
                          </span>
                        </div>

                        <div className="relative h-24 w-24 transition-transform duration-300 group-hover:scale-105 sm:h-28 sm:w-28 md:h-32 md:w-32">
                          <div
                            className="absolute -inset-1 rounded-full opacity-50 blur-lg transition-opacity duration-300 group-hover:opacity-100"
                            style={{
                              background: `radial-gradient(circle, ${
                                ohiScore >= 60
                                  ? "rgba(34,197,94,0.2)"
                                  : ohiScore >= 40
                                  ? "rgba(245,158,11,0.2)"
                                  : "rgba(239,68,68,0.2)"
                              }, transparent 70%)`,
                            }}
                          />

                          <svg
                            className="relative h-full w-full -rotate-90"
                            viewBox="0 0 100 100"
                          >
                            <circle
                              cx="50"
                              cy="50"
                              r="38"
                              fill="none"
                              stroke="#e5e7eb"
                              strokeWidth="8"
                              className="dark:stroke-navy-600"
                            />
                            <circle
                              cx="50"
                              cy="50"
                              r="38"
                              fill="none"
                              stroke={
                                ohiScore >= 60
                                  ? "#22c55e"
                                  : ohiScore >= 40
                                  ? "#f59e0b"
                                  : "#ef4444"
                              }
                              strokeWidth="8"
                              strokeLinecap="round"
                              strokeDasharray={`${ohiScore * 2.387} 238.76`}
                              strokeDashoffset="0"
                              className="transition-all duration-1000"
                            />
                          </svg>

                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <motion.span
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: 0.8, type: "spring" }}
                              className="text-sm font-extrabold text-gray-900 dark:text-white sm:text-base"
                            >
                              {ohiScore.toFixed(1)}
                            </motion.span>
                            <span className="text-[12px] font-medium text-gray-700 dark:text-gray-400 md:text-sm">
                              %
                            </span>
                          </div>
                        </div>

                        <div className="mt-1 text-center">
                          <span
                            className={`text-[10px] font-bold sm:text-xs ${
                              ohiScore >= 60
                                ? "text-green-600 dark:text-green-400"
                                : ohiScore >= 40
                                ? "text-yellow-600 dark:text-yellow-400"
                                : "text-red-600 dark:text-red-400"
                            }`}
                          >
                            {ohiScore >= 60
                              ? t("assessment.report.status.good")
                              : ohiScore >= 40
                              ? t("assessment.report.status.average")
                              : t("assessment.report.status.needsImprovement")}
                          </span>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.7 }}
                        className="flex h-24 w-24 items-center justify-center rounded-full border-2 border-dashed border-gray-300 text-center text-[10px] text-gray-600 dark:border-gray-600 dark:text-gray-500 sm:h-28 sm:w-28 md:h-32 md:w-32"
                      >
                        {t("organizationAssessmentReport.noCompanyAssessment")}
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>

              {/* Gradient bottom line with animation */}
              <motion.div
                className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-brand-500 via-purple-500 to-pink-500"
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ delay: 0.5, duration: 0.8 }}
              />
            </div>
          </motion.div>
        )}

        {/* 4. Meta Info with Company Assessment Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-8 flex flex-wrap items-center gap-6 rounded-2xl bg-white/70 p-4 text-sm text-gray-700 backdrop-blur-sm dark:bg-navy-800/70 dark:text-gray-400"
        >
          <div className="flex items-center gap-2">
            <FaCalendarAlt className="h-4 w-4" />
            <span>
              {t("organizationAssessmentReport.createdAt")}:{" "}
              <span className="font-medium text-gray-900 dark:text-white">
                {moment(assessment.createdAt).format(
                  lang === "fa" ? "jYYYY/jMM/jDD" : "YYYY/MM/DD"
                )}
              </span>
            </span>
          </div>
          {isCompleted && assessment.completedAt && (
            <div className="flex items-center gap-2">
              <HiOutlineCheckCircle className="h-4 w-4" />
              <span>
                {t("organizationAssessmentReport.completedAt")}:{" "}
                <span className="font-medium text-gray-900 dark:text-white">
                  {moment(assessment.completedAt).format(
                    lang === "fa"
                      ? "jYYYY/jMM/jDD - HH:mm"
                      : "YYYY/MM/DD - HH:mm"
                  )}
                </span>
              </span>
            </div>
          )}

          {/* Link to Company Assessment */}
          {hasCompanyAssessment && companyAssessment?.id && (
            <div className="flex items-center gap-2">
              <Link
                to={`/${scope}/assessment/${companyAssessment.id}/report`}
                // target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-lg bg-brand-50 px-3 py-1.5 text-sm font-medium text-brand-600 transition-all hover:bg-brand-100 hover:text-brand-700 dark:bg-brand-500/20 dark:text-gray-200 dark:hover:bg-brand-500/30"
              >
                <HiOutlineExternalLink className="h-4 w-4" />
                {t("organizationAssessmentReport.viewCompanyAssessment")}
              </Link>
            </div>
          )}
        </motion.div>

        {/* 5. Chart Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-8"
        >
          <div className="rounded-3xl bg-white p-6 shadow-xl shadow-gray-200/50 dark:bg-navy-800 dark:shadow-navy-900/50">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {t("organizationAssessmentReport.questionScores")}
              </h3>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-3 w-3 rounded-full bg-brand-500" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {t("organizationAssessmentReport.IEII")}
                  </span>
                </div>
                {chartData && chartData.length > 0 && (
                  <span className="rounded-lg bg-brand-50 px-4 py-2 font-bold text-brand-600 dark:bg-brand-500/20 dark:text-brand-200">
                    {assessment.finalScore?.toFixed(1) || 0} %
                  </span>
                )}
              </div>
            </div>

            {isPending ? (
              <div className="rounded-xl border-2 border-dashed border-gray-200 p-12 text-center dark:border-gray-700">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400">
                  <HiOutlineXCircle className="h-8 w-8" />
                </div>
                <h4 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                  {t("organizationAssessmentReport.assessmentPending")}
                </h4>
                <p className="text-sm text-gray-700 dark:text-gray-400">
                  {t("organizationAssessmentReport.assessmentPendingDesc")}
                </p>
              </div>
            ) : !chartData || chartData.length === 0 ? (
              <div className="rounded-xl border-2 border-dashed border-gray-200 p-12 text-center dark:border-gray-700">
                <FaChartBar className="mx-auto h-12 w-12 text-gray-300" />
                <p className="mt-2 text-sm text-gray-700 dark:text-gray-400">
                  {t("organizationAssessmentReport.noDataForChart")}
                </p>
              </div>
            ) : (
              <div className="h-[500px] w-full md:h-[600px]">
                <canvas ref={chartRef} key={chartKey} />
              </div>
            )}
          </div>
        </motion.div>

        {/* 6. Industry LifeCycle Section - Below chart */}
        {isCompleted && hasCompanyAssessment && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mb-8"
          >
            <div className="rounded-3xl bg-white p-4 shadow-xl shadow-gray-200/50 dark:bg-navy-800 dark:shadow-navy-900/50">
              <IndustryLifeCycle
                company={assessment.company}
                score={companyAssessment?.reports?.lifeCycle?.score || 0}
                position={companyAssessment?.reports?.lifeCycle?.position}
                showBottomCards={false}
              />
            </div>
          </motion.div>
        )}

        {/* 7. Responders Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="rounded-3xl bg-white p-3 shadow-xl shadow-gray-200/50 dark:bg-navy-800 dark:shadow-navy-900/50 lg:p-4"
        >
          <h3 className="mb-6 text-xl font-bold text-gray-900 dark:text-white">
            {t("organizationAssessmentReport.respondersList")}
          </h3>

          {respondersWithStatus.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-gray-200 p-12 text-center dark:border-gray-700">
              <HiOutlineUserGroup className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-2 text-sm text-gray-700 dark:text-gray-400">
                {t("organizationAssessmentReport.noResponders")}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {respondersWithStatus
                ?.sort((a, b) => {
                  if (a.answers.length > 0 && b.answers.length === 0) return -1;
                  if (a.answers.length === 0 && b.answers.length > 0) return 1;
                  return 0;
                })
                ?.map((responder: any, index: number) => {
                  const isExpanded = expandedResponderId === responder.id;
                  const hasAnswers = responder.answers.length > 0;

                  return (
                    <motion.div
                      key={responder.id || index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 + 0.8 }}
                      className="overflow-hidden rounded-xl border border-gray-200 transition-all hover:border-brand-200 dark:border-gray-700 dark:hover:border-brand-700"
                    >
                      <div
                        className={`flex w-full flex-wrap items-center justify-between gap-2 p-4 text-start transition-colors ${
                          hasAnswers
                            ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-700/50"
                            : "cursor-default"
                        }`}
                        onClick={() =>
                          hasAnswers &&
                          setExpandedResponderId(
                            isExpanded ? null : responder.id
                          )
                        }
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-white shadow-md">
                            <span className="font-bold">
                              {responder?.responder?.name?.charAt(0) || "?"}
                            </span>
                          </div>
                          <div>
                            <p className="text-start font-semibold text-gray-900 dark:text-white">
                              {responder?.responder?.name || "-"}
                            </p>
                            <p className="text-start text-xs text-gray-700 dark:text-gray-400">
                              {t("responder.nationalCode")}:{" "}
                              {responder?.responder?.nationalCode || "-"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {hasAnswers ? (
                            <>
                              <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-600 dark:bg-green-500/20 dark:text-green-400">
                                {responder.answers.length}{" "}
                                {t("organizationAssessmentReport.answers")}
                              </span>
                              {isExpanded ? (
                                <HiOutlineChevronUp className="h-5 w-5 text-gray-400" />
                              ) : (
                                <HiOutlineChevronDown className="h-5 w-5 text-gray-400" />
                              )}
                            </>
                          ) : (
                            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                              {t("organizationAssessmentReport.noResponseYet")}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* responses */}
                      <AnimatePresence>
                        {hasAnswers && isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="border-t border-gray-100 px-4 pb-4 pt-3 dark:border-gray-700"
                          >
                            <div className="space-y-3">
                              {responder.answers.map(
                                (answer: any, idx: number) => (
                                  <div
                                    key={idx}
                                    className="flex items-start gap-2 rounded-lg bg-gray-50 p-3 text-sm dark:bg-navy-700/50"
                                  >
                                    <span className="mt-0.5 text-xs font-bold text-gray-700">
                                      {idx + 1}.
                                    </span>
                                    <div className="flex-1">
                                      <p className="text-gray-900 dark:text-gray-300">
                                        {answer.questionName}
                                      </p>
                                      <div className="mt-1 flex flex-wrap items-center gap-2">
                                        <span
                                          className={`inline-flex items-center gap-1 rounded-full bg-purple-100 px-3 py-1 text-sm font-medium text-purple-700 dark:bg-purple-500/20 dark:text-purple-400`}
                                        >
                                          {t(`option.${answer.optionKey}`)}
                                        </span>
                                        {answer.description && (
                                          <span className="text-sm text-gray-700 dark:text-gray-300">
                                            {t(
                                              "organizationAssessmentReport.description"
                                            )}{" "}
                                            - {answer.description}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
            </div>
          )}
        </motion.div>
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={confirmModal.closeModal}
        handler={handleCompleteOrganizationAssessment}
        loading={isCompleting}
        primary={true}
        title={t("organizationAssessmentReport.modalTitle")}
        desc={t("organizationAssessmentReport.modalDesc")}
      />
    </div>
  );
}
